/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – OverviewPanel.tsx
// ============================================================

import React, { useMemo } from 'react';
import {
  AlertOutlined, EnvironmentOutlined, WarningOutlined,
  ThunderboltOutlined, ApartmentOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

import {
  dailyAlertStats, mockWeather, contaminantStats,
  mockFarms, mockWQI, mockCompliance,
} from '../../data/mockData';
import { SensorStatus } from '../../types';
import { useSimulatorData } from '../../context/SimulatorContext';
import SensorMap from '../../components/SensorMap';

// ─── Constante ────────────────────────────────────────────

const SC: Record<SensorStatus, string> = {
  online: '#10b981', warning: '#f59e0b',
  offline: '#ef4444', calibrating: '#6366f1',
};
const SL: Record<SensorStatus, string> = {
  online: 'Online', warning: 'Avertisment',
  offline: 'Offline', calibrating: 'Calibrare',
};

// ─── KPI Card ─────────────────────────────────────────────

const KpiCard: React.FC<{
  title: string; value: string | number; sub?: string;
  icon: React.ReactNode; color: string; bg: string;
  badge?: { text: string; color: string; bg: string };
}> = ({ title, value, sub, icon, color, bg, badge }) => (
  <div style={{
    background: '#fff', borderRadius: 14,
    border: '1px solid #e2e8f0', padding: '16px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', letterSpacing: '0.04em' }}>
        {title.toUpperCase()}
      </div>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: bg, fontSize: 15, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color, letterSpacing: '-0.5px', lineHeight: 1 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{sub}</div>}
    {badge && (
      <div style={{ display: 'inline-flex', alignItems: 'center', marginTop: 8, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: badge.bg, color: badge.color, alignSelf: 'flex-start' }}>
        {badge.text}
      </div>
    )}
  </div>
);

// ─── Meteo Widget ─────────────────────────────────────────

const WeatherWidget: React.FC<{ tick: number }> = ({ tick }) => {
  const w = useMemo(() => {
    const delta = ((tick % 10) - 5) * 0.2;
    return {
      ...mockWeather,
      temperature: parseFloat((mockWeather.temperature + delta).toFixed(1)),
      humidity:    Math.min(100, Math.max(30, mockWeather.humidity + Math.round(delta * 2))),
      windSpeed:   Math.max(0, mockWeather.windSpeed + Math.round(delta * 3)),
      updatedAt:   new Date().toISOString(),
    };
  }, [tick]);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1e3a5f, #2d5986)',
      borderRadius: 14, padding: '18px 20px',
      color: '#fff', position: 'relative', overflow: 'hidden', height: '100%',
      boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    }}>
      <div style={{ position: 'absolute', top: -10, right: -10, fontSize: 72, opacity: 0.07 }}>{w.icon}</div>

      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em', marginBottom: 10 }}>
          METEO LIVE · {w.location}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <span style={{ fontSize: 44, lineHeight: 1 }}>{w.icon}</span>
          <div>
            <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1 }}>{w.temperature}°C</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>{w.condition}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {[
          { l: 'Umiditate', v: `${w.humidity}%` },
          { l: 'Vânt',      v: `${w.windSpeed} km/h ${w.windDirection}` },
          { l: 'Presiune',  v: `${w.pressure} hPa` },
        ].map((m, i) => (
          <div key={i} style={{ background: 'rgba(255,255,255,0.09)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginBottom: 2 }}>{m.l}</div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{m.v}</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', marginTop: 10, textAlign: 'right' }}>
        Actualizat: {new Date(w.updatedAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

// ─── Section Card wrapper ─────────────────────────────────

const Card: React.FC<{
  title: string; sub?: string; children: React.ReactNode; noPad?: boolean;
}> = ({ title, sub, children, noPad }) => (
  <div style={{
    background: '#fff', borderRadius: 14,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
  }}>
    <div style={{ padding: '13px 20px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
    <div style={{ flex: 1, padding: noPad ? 0 : '14px 20px', overflow: 'hidden' }}>
      {children}
    </div>
  </div>
);

// ─── Panel principal ──────────────────────────────────────

const OverviewPanel: React.FC = () => {
  const { sensors, alerts, lastUpdate, tickCount } = useSimulatorData();

  const online      = sensors.filter(s => s.status === 'online').length;
  const warning     = sensors.filter(s => s.status === 'warning').length;
  const offline     = sensors.filter(s => s.status === 'offline').length;
  const calibrating = sensors.filter(s => s.status === 'calibrating').length;
  const active      = alerts.filter(a => a.status === 'active').length;
  const critical    = alerts.filter(a => a.severity === 'critical' && a.status === 'active').length;
  const overLod     = sensors.filter(s => s.value >= s.lod && s.status !== 'offline').length;

  const avgBattery = useMemo(() => {
    const b = sensors.filter(s => s.batteryLevel != null);
    return b.length ? Math.round(b.reduce((s, x) => s + (x.batteryLevel ?? 0), 0) / b.length) : 0;
  }, [sensors]);

  const avgWQI    = Math.round(mockWQI.reduce((s, w) => s + w.score, 0) / mockWQI.length);
  const wqiColor  = avgWQI >= 70 ? '#10b981' : avgWQI >= 50 ? '#f59e0b' : '#ef4444';
  const wqiLabel  = avgWQI >= 85 ? 'Excelent' : avgWQI >= 70 ? 'Bun' : avgWQI >= 50 ? 'Moderat' : avgWQI >= 25 ? 'Slab' : 'Critic';
  const compRate  = Math.round((mockCompliance.compliant / mockCompliance.totalChecked) * 100);
  const compColor = compRate >= 80 ? '#10b981' : '#f59e0b';

  const statusPie = [
    { name: 'Online',      value: online,      color: '#10b981' },
    { name: 'Avertisment', value: warning,      color: '#f59e0b' },
    { name: 'Offline',     value: offline,      color: '#ef4444' },
    { name: 'Calibrare',   value: calibrating,  color: '#6366f1' },
  ].filter(s => s.value > 0);

  const topSensors = sensors
    .filter(s => s.status !== 'offline' && s.value > 0)
    .sort((a, b) => (b.value / b.lod) - (a.value / a.lod))
    .slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>General</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {sensors.length} senzori · {mockFarms.length} ferme · Actualizat {lastUpdate.toLocaleTimeString('ro-RO')}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: `${wqiColor}12`, border: `1px solid ${wqiColor}30`, color: wqiColor }}>
            <ApartmentOutlined /> WQI {avgWQI}/100 — {wqiLabel}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: `${compColor}12`, border: `1px solid ${compColor}30`, color: compColor }}>
            <SafetyCertificateOutlined /> Conformitate {compRate}%
          </span>
        </div>
      </div>

      {/* ── Rândul 1: KPI-uri ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <KpiCard
          title="Senzori online" value={`${online} / ${sensors.length}`}
          icon={<EnvironmentOutlined />} color="#10b981" bg="#d1fae5"
          sub={`${warning} avertisment · ${offline} offline`}
          badge={offline > 0
            ? { text: `${offline} offline`, color: '#ef4444', bg: '#fee2e2' }
            : { text: 'Toți conectați', color: '#059669', bg: '#d1fae5' }}
        />
        <KpiCard
          title="Alerte active" value={active}
          icon={<AlertOutlined />} color="#ef4444" bg="#fee2e2"
          sub="Necesită intervenție"
          badge={critical > 0
            ? { text: `${critical} critice`, color: '#7f1d1d', bg: '#fecaca' }
            : { text: 'Fără critice', color: '#059669', bg: '#d1fae5' }}
        />
        <KpiCard
          title="Depășiri LOD" value={overLod}
          icon={<WarningOutlined />} color="#f59e0b" bg="#fef3c7"
          sub="Contaminanți detectați"
          badge={{ text: `din ${sensors.filter(s => s.status !== 'offline').length} activi`, color: '#92400e', bg: '#fef9c3' }}
        />
        <KpiCard
          title="Baterie medie" value={`${avgBattery}%`}
          icon={<ThunderboltOutlined />} color="#6366f1" bg="#eef2ff"
          sub="Medie flotă senzori"
          badge={avgBattery < 30
            ? { text: 'Atenție', color: '#ef4444', bg: '#fee2e2' }
            : { text: 'Nivel OK', color: '#059669', bg: '#d1fae5' }}
        />
      </div>

      {/* ── Rândul 2: Hartă (stânga mare) + Status Senzori + Meteo (dreapta) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 14, alignItems: 'stretch' }}>

        {/* Hartă */}
        <Card title="Hartă senzori" noPad
          sub={`${online} online · ${warning} avertisment · ${offline} offline · ${calibrating} calibrare`}
        >
          <SensorMap height={280} showFilter showLegend />
        </Card>

        {/* Status + Meteo stivuite */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Status senzori — pie chart */}
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '13px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Status senzori</div>
            <ResponsiveContainer width="100%" height={120}>
              <PieChart>
                <Pie data={statusPie} cx="40%" cy="50%" innerRadius={32} outerRadius={50} paddingAngle={3} dataKey="value">
                  {statusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Legend
                  layout="vertical" align="right" verticalAlign="middle"
                  iconSize={8} wrapperStyle={{ fontSize: 10, lineHeight: '18px' }}
                />
                <RTooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Meteo */}
          <div style={{ flex: 1 }}>
            <WeatherWidget tick={tickCount} />
          </div>
        </div>
      </div>

      {/* ── Rândul 3: Grafice alerte + Contaminanți ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        <Card title="Alerte — ultimele 14 zile" sub="Stacked per severitate">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={dailyAlertStats.slice(-14)} margin={{ top: 4, right: 4, bottom: 0, left: -28 }} barSize={11}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={2} />
              <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11, border: '1px solid #e2e8f0' }} />
              <Bar dataKey="critical" name="Critic"  stackId="a" fill="#ef4444" />
              <Bar dataKey="high"     name="Ridicat" stackId="a" fill="#f97316" />
              <Bar dataKey="medium"   name="Mediu"   stackId="a" fill="#f59e0b" />
              <Bar dataKey="low"      name="Scăzut"  stackId="a" fill="#84cc16" radius={[2,2,0,0]} />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Distribuție contaminanți" sub="Senzori per substanță monitorizată">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={contaminantStats} cx="40%" cy="50%" innerRadius={42} outerRadius={65} paddingAngle={3} dataKey="count">
                {contaminantStats.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Legend
                layout="vertical" align="right" verticalAlign="middle"
                iconSize={8} wrapperStyle={{ fontSize: 10, lineHeight: '18px' }}
              />
              <RTooltip contentStyle={{ borderRadius: 8, fontSize: 11 }} formatter={(v: any, n: any) => [v, `${n} senzori`]} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ── Rândul 4: Top senzori față de LOD ── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '13px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Senzori cu valori ridicate față de LOD</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Sortați descrescător · Top 5</div>
        </div>

        {/* Header tabel */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.8fr 1fr 90px', gap: 12, padding: '8px 20px', background: '#f8fafc', fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>
          {['SENZOR', 'VALOARE', '% DIN LOD', 'FERMĂ', 'STATUS'].map(h => <div key={h}>{h}</div>)}
        </div>

        {topSensors.map((s, i) => {
          const pct   = Math.round((s.value / s.lod) * 100);
          const color = SC[s.status];
          return (
            <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.8fr 1fr 90px', gap: 12, padding: '12px 20px', alignItems: 'center', borderBottom: i < topSensors.length - 1 ? '1px solid #f8fafc' : 'none', transition: 'background 0.15s' }}
              onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = '#fafbff'}
              onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{s.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 3 }}>
                  <EnvironmentOutlined style={{ fontSize: 9 }} /> {s.location.label}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: s.value >= s.lod ? '#ef4444' : '#10b981' }}>{s.value} {s.unit}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>LOD: {s.lod}</div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{s.parameter}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color }}>{pct}%</span>
                </div>
                <div style={{ height: 5, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 99, width: `${Math.min(pct, 100)}%`, background: pct >= 150 ? '#ef4444' : pct >= 100 ? '#f97316' : '#f59e0b' }} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.farmName?.split('–')[0].trim() ?? '–'}
              </div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: `${color}15`, color }}>
                  {SL[s.status]}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Rândul 5: WQI per fermă ── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '13px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Indice calitate apă (WQI) per fermă</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Scor 0–100 · Verde ≥70 · Galben 50–69 · Roșu &lt;50</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {mockWQI.map((w, i) => {
            const c   = w.score >= 70 ? '#10b981' : w.score >= 50 ? '#f59e0b' : '#ef4444';
            const bg  = w.score >= 70 ? '#d1fae5' : w.score >= 50 ? '#fef3c7' : '#fee2e2';
            const lbl = w.score >= 85 ? 'Excelent' : w.score >= 70 ? 'Bun' : w.score >= 50 ? 'Moderat' : w.score >= 25 ? 'Slab' : 'Critic';
            return (
              <div key={w.farmId} style={{ padding: '14px 20px', borderRight: (i + 1) % 3 !== 0 ? '1px solid #f1f5f9' : 'none', borderBottom: i < mockWQI.length - 3 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {w.farmName.split('–')[0].replace('Ferma ', '').trim()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, height: 6, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 99, width: `${w.score}%`, background: c }} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 800, color: c, minWidth: 28 }}>{w.score}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: bg, color: c, flexShrink: 0 }}>{lbl}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default OverviewPanel;